import React, { useMemo, useRef } from 'react';
import { ApolloProvider } from '@apollo/client';
import { createClient } from 'client/utils/apollo';
import { useClientConfig } from './ClientConfigContext';

type WorkerComplianceApolloProviderProps = {
    accountKey: string;
    injectedGraphQlVariables?: string[];
};

export const WorkerComplianceApolloProvider = ({
    children,
    accountKey,
    injectedGraphQlVariables,
}: React.PropsWithChildren<WorkerComplianceApolloProviderProps>): JSX.Element => {
    const clientConfig = useClientConfig();
    const injectedGraphQlVariablesRef = useRef(injectedGraphQlVariables);
    if (!clientConfig.backendUrl) {
        throw new Error('Backend URL is not configured');
    }

    /**
     * Since this value is an array passing it to useMemo can trigger the generation of a new client
     * A new client would have a seperate cache
     * Additionally the injectedGraphQlVariables is a constant value so it should not change anyway
     * In practice this value is set in src/client/apps/WorkerComplianceApp.tsx as a constant value outside of a component
     */
    if (injectedGraphQlVariablesRef.current !== injectedGraphQlVariables) {
        throw new Error(
            'Reference for `injectedGraphQlVariables` changed, this will cause the client to be regenerated... Please memoize injectedGraphQlVariables'
        );
    }
    const backendUrl = clientConfig.backendUrl;

    const client = useMemo(
        () => createClient(backendUrl, accountKey, injectedGraphQlVariablesRef.current),
        [backendUrl, accountKey]
    );

    return <ApolloProvider client={client}>{children}</ApolloProvider>;
};
